import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { apiGetTrialUsageJournal, apiGenerateTrialUsageJournal } from '../api/backend.js';
import UsageHeatmap, { isCountedEvent, dedupeDashboardEvents, ptDate, ptTime, TIMEFRAMES } from './UsageHeatmap.jsx';

// UsageJournalPanel — Amplitude usage journal for an In Progress trial
// (IMP-1757). Layout: summary (snapshot + per-user rollup) first, then the
// noticeable gaps, then the daily interaction summary — the long day-by-day
// part — in its own collapsible box. Generation runs in the background on the
// server; while regenerating, the existing journal stays readable under a
// pulse animation (history doesn't change — new days are added on top).

const POLL_MS = 5000;
const POLL_TIMEOUT_MS = 20 * 60 * 1000;
const STALE_MS = 24 * 60 * 60 * 1000;

const VERDICT_STYLE = {
  champion:      { color: 'var(--green)', label: 'Champion' },
  warm:          { color: 'var(--amber)', label: 'Warm' },
  ghosted:       { color: 'var(--red)',   label: 'Ghosted' },
  browsing_only: { color: 'var(--text-3)', label: 'Browsing only' },
};

export default function UsageJournalPanel({ trial, getToken }) {
  const [state, setState]     = useState(null);   // GET response
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);  // POST in flight or polling
  const [timeframe, setTimeframe] = useState(7);  // shared by heatmap + stats row (IMP-1852)
  const [busyMode, setBusyMode] = useState(null); // 'events' | 'full' | null (unknown/other client)
  const pollRef = useRef(null);
  const autoRef = useRef(null);  // trial_id already auto-refreshed this mount

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const load = useCallback(async () => {
    try {
      const data = await apiGetTrialUsageJournal(getToken, trial.trial_id);
      setState(data);
      setError('');
      return data;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getToken, trial.trial_id]);

  useEffect(() => {
    load().then((data) => {
      if (!data) return;
      if (data.running) { startPolling(); return; }
      // Auto-refresh (IMP-1847): opening a user always refreshes the heatmap
      // via an events-only run — no LLM calls, and incremental after the
      // first run, so it's cheap. The journal (narrative) only regenerates
      // when the button is clicked.
      if (data.configured === false || autoRef.current === trial.trial_id) return;
      autoRef.current = trial.trial_id;
      handleGenerate({ eventsOnly: true });
    });
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trial.trial_id]);

  // The backend marks the trial in-flight before the POST returns, so we can
  // simply poll until `running` flips back to false.
  function startPolling() {
    stopPolling();
    setBusy(true);
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    pollRef.current = setInterval(async () => {
      const data = await load();
      if (data && !data.running) {
        stopPolling(); setBusy(false); setBusyMode(null);
        // "Running" ended with nothing stored: that was a phantom row from a
        // crashed run (the backend now expires those in ~3 min), not a real
        // one — so the mount-time auto-refresh was skipped. Run it now.
        if (!data.latest && data.configured !== false && autoRef.current !== trial.trial_id) {
          autoRef.current = trial.trial_id;
          handleGenerate({ eventsOnly: true });
        }
        return;
      }
      if (Date.now() > deadline) { stopPolling(); setBusy(false); setBusyMode(null); setError('Journal generation timed out — try again.'); }
    }, POLL_MS);
  }

  async function handleGenerate({ eventsOnly = false, days } = {}) {
    setBusy(true);
    setBusyMode(eventsOnly ? 'events' : 'full');
    setError('');
    try {
      await apiGenerateTrialUsageJournal(getToken, trial.trial_id, { eventsOnly, days });
      startPolling();
    } catch (e) {
      setBusy(false);
      setBusyMode(null);
      setError(e.message);
    }
  }

  // Selecting a timeframe wider than the exported window kicks off an
  // events-only refresh with a bigger window (IMP-1852). A requested start
  // earlier than the stored window_start makes the backend do a full
  // re-export (the incremental guard rejects it), so history fills in.
  const widerReqRef = useRef(0);
  function handleTimeframeChange(d) {
    setTimeframe(d);
    const l = state?.latest;
    if (!l || l.status !== 'done' || busy || state?.running || state?.configured === false) return;
    const neededStart = new Date(Date.now() - d * 86_400_000).toISOString().slice(0, 10);
    if (String(l.window_start ?? '') > neededStart && widerReqRef.current < d) {
      widerReqRef.current = d;
      handleGenerate({ eventsOnly: true, days: Math.min(190, d + 7) });
    }
  }

  const latest  = state?.latest ?? null;
  const journal = latest?.status === 'done' ? latest.journal : null;
  // Deduped once here so the heatmap, its tooltips, and the stats row all
  // agree (same ticker + same minute = one dashboard view).
  const events  = useMemo(
    () => (latest?.status === 'done' && latest.events ? dedupeDashboardEvents(latest.events) : null),
    [latest],
  );
  const running = busy || state?.running;
  // Any run refreshes the events store; only a full run touches the narrative.
  // busyMode === null while running means another client started it (mode
  // unknown) — treat both sections as refreshing.
  // The routine on-open refresh is SILENT (IMP-1847 follow-up): the stored
  // heatmap is already rendered and correct, so pulse only when what's on
  // screen is actually incomplete — no events yet (first load), or the
  // selected timeframe reaches back past the stored window.
  const _neededStart = new Date(Date.now() - timeframe * 86_400_000).toISOString().slice(0, 10);
  const windowCovered = Boolean(latest?.window_start && String(latest.window_start) <= _neededStart);
  const heatmapBusy = running && (!events?.length || !windowCovered);
  const journalBusy = running && busyMode !== 'events';
  const ageMs   = latest?.generated_at ? Date.now() - new Date(latest.generated_at).getTime() : null;
  const isStale = Boolean(journal && !running && Number.isFinite(ageMs) && ageMs > STALE_MS);

  const sectionTitle = { fontSize: 10, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.5px' };

  // Stats row synced to the heatmap timeframe (IMP-1852). Hook, so it must
  // sit ABOVE the loading early-return (hooks must run on every render).
  // EVENTS counts only
  // feature + stock-dashboard events (isCountedEvent); users/first/last/
  // silent-days use ALL activity — they answer "were they in the product".
  const windowStats = useMemo(() => {
    if (!events?.length) return null;
    const cutoff = Date.now() - timeframe * 86_400_000;
    const inWin = events.filter((ev) => ev.utcMs >= cutoff);
    if (!inWin.length) {
      return { seats: 0, totalEvents: 0, silentDays: null, firstActivity: null, lastActivity: null, dau: [] };
    }
    let firstMs = Infinity, lastMs = -Infinity, counted = 0;
    const users = new Set();
    const byDay = new Map();
    for (const ev of inWin) {
      if (ev.utcMs < firstMs) firstMs = ev.utcMs;
      if (ev.utcMs > lastMs) lastMs = ev.utcMs;
      if (isCountedEvent(ev)) counted++;
      if (ev.uid) users.add(ev.uid);
      const d = ptDate(ev.utcMs);
      if (!byDay.has(d)) byDay.set(d, new Set());
      if (ev.uid) byDay.get(d).add(ev.uid);
    }
    const dau = [...byDay.entries()]
      .map(([date, set]) => ({ date, activeUsers: set.size }))
      .sort((a, b) => a.date.localeCompare(b.date));
    return {
      seats: users.size,
      totalEvents: counted,
      silentDays: Math.floor((Date.now() - lastMs) / 86_400_000),
      firstActivity: `${ptDate(firstMs)} ${ptTime(firstMs)}`,
      lastActivity: `${ptDate(lastMs)} ${ptTime(lastMs)}`,
      dau,
    };
  }, [events, timeframe]);
  const timeframeLabel = TIMEFRAMES.find((t) => t.days === timeframe)?.label ?? `${timeframe} days`;

  if (loading) return <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>Loading usage journal…</span>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {state?.configured === false && (
        <div style={{ fontSize: 11.5, color: 'var(--amber)' }}>
          Amplitude Export API keys are not configured on the backend (AMPLITUDE_API_KEY / AMPLITUDE_SECRET_KEY).
        </div>
      )}
      {error && <div style={{ fontSize: 11.5, color: 'var(--red)' }}>{error}</div>}

      {/* ── Usage Heatmap — auto-refreshes on every open (events-only, no LLM) ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={sectionTitle}>Usage Heatmap</span>
          {heatmapBusy ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--text-3)' }}>
              <span className="spinner" style={{ width: 10, height: 10 }} />
              refreshing
            </span>
          ) : latest?.generated_at ? (
            <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>updated {_ago(latest.generated_at)}</span>
          ) : null}
        </div>
        {events && events.length > 0 ? (
          <div style={heatmapBusy ? { animation: 'bd-pulse 1.6s ease-in-out infinite' } : undefined}>
            <UsageHeatmap events={events} timeframe={timeframe} onTimeframeChange={handleTimeframeChange} />
          </div>
        ) : heatmapBusy ? (
          <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>
            Loading usage heatmap… (first load pulls the full window and can take a minute)
          </span>
        ) : (
          <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>
            No usage data yet.
          </span>
        )}
      </div>

      {/* ── Usage Journal — regenerates only on click ── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={sectionTitle}>Usage Journal</span>
          {latest?.generated_at && journal && (
            <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
              window {latest.window_start} → {latest.window_end}
            </span>
          )}
          {isStale && (
            <span
              title={`Journal is ${_age(latest.generated_at)} old — regenerate to add the latest activity`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 10, fontWeight: 800, color: 'var(--amber)',
                border: '1px solid var(--amber)', borderRadius: 999, padding: '1px 7px',
                animation: 'bd-pulse 1.4s ease-in-out infinite',
              }}
            >
              ⚠ {_age(latest.generated_at)} old
            </span>
          )}
          <button
            className="btn btn--ghost btn--sm"
            style={{ fontSize: 11, marginLeft: 'auto', ...(isStale ? { color: 'var(--amber)', borderColor: 'var(--amber)' } : {}) }}
            onClick={() => handleGenerate()}
            disabled={running || state?.configured === false}
            title={state?.configured === false ? 'Amplitude API keys not configured on the backend' : 'Pull new Amplitude activity onto this journal (history is kept as-is)'}
          >
            {journalBusy
              ? <><span className="spinner" style={{ width: 11, height: 11 }} /> {journal ? 'Updating…' : 'Generating… (can take a few min)'}</>
              : journal ? '⟳ Regenerate journal' : 'Generate journal'}
          </button>
        </div>

        {latest?.status === 'error' && !running && (
          <div style={{ fontSize: 11.5, color: 'var(--red)', marginBottom: 6 }}>Last generation failed: {latest.error}</div>
        )}
        {journal ? (
          <div style={journalBusy ? { animation: 'bd-pulse 1.6s ease-in-out infinite' } : undefined}>
            <JournalBody journal={journal} narrative={latest.narrative} statsOverride={windowStats} statsNote={`last ${timeframeLabel}`} />
          </div>
        ) : !journalBusy && state?.configured !== false ? (
          <span style={{ fontSize: 11.5, color: 'var(--text-3)', fontStyle: 'italic' }}>
            No journal yet — generate one to get a call-prep usage writeup from Amplitude.
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ── Journal rendering ─────────────────────────────────────────────────────────

function JournalBody({ journal, narrative, statsOverride, statsNote }) {
  const [showDays, setShowDays] = useState(false);
  const [showRaw, setShowRaw]   = useState(false);
  // statsOverride (IMP-1852): deterministic stats recomputed client-side for
  // the heatmap's timeframe; the stored snapshot is the whole-window fallback.
  const s = statsOverride ?? journal.snapshot ?? {};
  const people = Array.isArray(journal.people) ? journal.people : [];

  // Noticeable patterns — AI-written (good or bad habits) on new journals;
  // older journals fall back to the deterministic zeros.
  const patterns = Array.isArray(journal.patterns) && journal.patterns.length
    ? journal.patterns
    : people.flatMap((p) => (p.zeros ?? []).map((z) => people.length > 1 ? `${p.name ?? p.email}: ${z}` : z));

  const hasDaily = Boolean(narrative) || (Array.isArray(journal.days) && journal.days.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* ── Summary ── */}
      {statsNote && (
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
          {statsNote}
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <Stat label="users seen" value={s.seats ?? '—'} />
        <Stat label="events" value={s.totalEvents ?? '—'} />
        <Stat
          label="silent days"
          value={s.silentDays ?? '—'}
          color={s.silentDays == null ? undefined : s.silentDays >= 7 ? 'var(--red)' : s.silentDays >= 3 ? 'var(--amber)' : 'var(--green)'}
        />
        <div style={{ fontSize: 10.5, color: 'var(--text-3)', alignSelf: 'flex-end' }}>
          {s.firstActivity ? <>first: {s.firstActivity}<br /></> : null}
          {s.lastActivity ? <>last: {s.lastActivity}</> : 'no activity in window'}
        </div>
      </div>

      {/* DAU mini bars */}
      {Array.isArray(s.dau) && s.dau.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 3 }}>Active days</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 26 }}>
            {s.dau.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.activeUsers} active`}
                style={{
                  flex: 1, maxWidth: 14, borderRadius: 2,
                  height: `${Math.max(3, (d.activeUsers / Math.max(...s.dau.map((x) => x.activeUsers), 1)) * 24)}px`,
                  background: 'var(--primary)',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Per-user rollup: name/email → summary → noticeable patterns */}
      {people.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {people.map((p, idx) => {
            const v = VERDICT_STYLE[p.verdict] ?? { color: 'var(--text-2)', label: p.verdict };
            const chat  = p.counts?.chat_question ?? 0;
            const proto = p.counts?.protege_message ?? 0;
            const calls = p.counts?.leave_room ?? 0;
            const showPatterns = idx === people.length - 1 && patterns.length > 0;
            return (
              <div key={p.email} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '6px 9px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{p.name ?? p.email}</span>
                  {p.name && <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{p.email}</span>}
                  {p.role && <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>· {p.role}</span>}
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: v.color, marginLeft: 'auto' }}>{v.label}</span>
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }}>
                  {p.activeDays} active day{p.activeDays !== 1 ? 's' : ''} · {p.sessions} session{p.sessions !== 1 ? 's' : ''} · {p.prompts} prompt{p.prompts !== 1 ? 's' : ''}{(chat > 0 || proto > 0) && <> ({chat} chat + {proto} Protege)</>} · {p.totalEvents} event{p.totalEvents !== 1 ? 's' : ''}{calls > 0 && <> · {calls} earnings call{calls !== 1 ? 's' : ''} attended</>} · last seen {p.lastSeen} ({p.daysSinceLast}d ago)
                </div>
                {/* Patterns span the journal, so they render once — on the last card. */}
                {showPatterns && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>
                      Noticeable patterns
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {patterns.map((g, i) => <li key={i} style={{ fontSize: 11, color: 'var(--text-2)' }}>{g}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Linked contacts with zero activity — worth knowing before a call */}
      {journal.contactsWithNoActivity?.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
          <span style={{ fontWeight: 700 }}>No activity in window from: </span>
          {journal.contactsWithNoActivity.map((c) => c.name ?? c.email).join(', ')}
        </div>
      )}

      {/* ── Daily interaction summary — its own box, collapsible ── */}
      {hasDaily && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2)' }}>
          <button
            onClick={() => setShowDays((x) => !x)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left',
              background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px',
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{showDays ? '▴' : '▾'}</span>
            <span style={{ fontSize: 13.5, fontWeight: 800 }}>Daily interaction summary</span>
            <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
              ({journal.days?.length ?? 0} active day{journal.days?.length !== 1 ? 's' : ''}, most recent first)
            </span>
          </button>
          {showDays && (
            <div style={{ padding: '0 12px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {narrative ? <MiniMarkdown text={narrative} /> : <RawDayLog days={journal.days} />}
              {narrative && journal.days?.length > 0 && (
                <div>
                  <button className="link" style={{ fontSize: 10.5, color: 'var(--text-3)' }} onClick={() => setShowRaw((x) => !x)}>
                    {showRaw ? '▴ hide raw event log' : '▾ raw event log'}
                  </button>
                  {showRaw && <div style={{ marginTop: 6 }}><RawDayLog days={journal.days} /></div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

function RawDayLog({ days }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {(days ?? []).map((d) => (
        <div key={d.date}>
          <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text)' }}>{_prettyDate(d.date)}</div>
          {d.users.map((u) => (
            <div key={u.email} style={{ marginLeft: 8, marginTop: 2 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{u.name ?? u.email}</div>
              {u.sessions.map((sess, i) => (
                <div key={i} style={{ marginLeft: 10, paddingLeft: 8, borderLeft: '2px solid var(--border)', marginTop: 3 }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                    {sess.serverSide ? 'server-side events' : `${sess.start}${sess.end !== sess.start ? ` – ${sess.end}` : ''}`}
                    {sess.pageViews > 0 && <> · {sess.pageViews} page view{sess.pageViews !== 1 ? 's' : ''}{sess.paths?.length ? ` (${sess.paths.slice(0, 5).join(', ')})` : ''}</>}
                  </div>
                  {sess.notable.map((ev, j) => (
                    <div key={j} style={{ fontSize: 11, color: 'var(--text)', marginTop: 1 }}>
                      <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{ev.time}</span> {ev.detail}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 800, color: color ?? 'var(--text)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// Minimal markdown for the narrative: #/##/### headers, **bold**, *italic*
// lines, - bullets. Everything is rendered through React (no innerHTML), so it
// stays safe.
function MiniMarkdown({ text }) {
  const lines = String(text).split('\n');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {lines.map((line, i) => {
        const h = line.match(/^(#{1,4})\s+(.*)/);
        if (h) return <div key={i} style={{ fontSize: h[1].length <= 2 ? 12.5 : 11.5, fontWeight: 800, marginTop: i ? 6 : 0 }}>{_bold(h[2])}</div>;
        // Day-header lines ("Fri Aug 21", optionally with a gloss) — bold the
        // date, keep the parenthetical muted.
        const day = line.match(/^((?:Mon|Tue|Wed|Thu|Fri|Sat|Sun) [A-Z][a-z]{2,8}\.? \d{1,2})\s*(.*)$/);
        if (day) {
          return (
            <div key={i} style={{ fontSize: 12.5, fontWeight: 800, marginTop: i ? 8 : 0 }}>
              {day[1]}{day[2] && <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-3)' }}> {day[2]}</span>}
            </div>
          );
        }
        const b = line.match(/^\s*[-*]\s+(.*)/);
        if (b) return <div key={i} style={{ fontSize: 11.5, paddingLeft: 14, position: 'relative' }}><span style={{ position: 'absolute', left: 4 }}>•</span>{_bold(b[1])}</div>;
        // Full-line italic — the deterministic "silent stretch" dividers.
        const it = line.match(/^\*([^*].*)\*$/);
        if (it) return <div key={i} style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>{it[1]}</div>;
        if (!line.trim()) return <div key={i} style={{ height: 4 }} />;
        return <div key={i} style={{ fontSize: 11.5, color: 'var(--text)' }}>{_bold(line)}</div>;
      })}
    </div>
  );
}

function _bold(s) {
  const parts = String(s).split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i}>{p}</strong> : p));
}

const _age = (ts) => _ago(ts).replace(/ ago$/, '');

function _ago(ts) {
  const ms = Date.now() - new Date(ts).getTime();
  if (!Number.isFinite(ms)) return String(ts);
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function _prettyDate(ymd) {
  const d = new Date(`${ymd}T12:00:00`);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : ymd;
}
